import { CostData } from '../types';
import { Recommendation } from '../types';

interface RecommendationRule {
  id: string;
  name: string;
  description: string;
  condition: (data: CostData) => boolean;
  priority: 'high' | 'medium' | 'low';
  generateRecommendation: (data: CostData) => Omit<Recommendation, 'id' | 'createdAt' | 'updatedAt'>;
}

class IntelligentRecommendationService {
  private rules: RecommendationRule[] = [
    {
      id: 'high-cost-ec2-instances',
      name: 'High Cost EC2 Instances',
      description: 'Detect EC2 instances with unusually high costs',
      condition: (data: CostData) => {
        const ec2Cost = data.clouds.aws.services.find(s => s.serviceName === 'EC2')?.totalCost || 0;
        const totalAwsCost = data.clouds.aws.totalSpend;
        return ec2Cost > 0 && (ec2Cost / totalAwsCost) > 0.4; // EC2 costs more than 40% of AWS spend
      },
      priority: 'high',
      generateRecommendation: (data: CostData) => {
        const ec2Cost = data.clouds.aws.services.find(s => s.serviceName === 'EC2')?.totalCost || 0;
        const potentialSavings = Math.round(ec2Cost * 0.25); // Assume 25% savings possible
        
        return {
          title: 'Optimize High-Cost EC2 Instances',
          description: `EC2 instances account for ${((ec2Cost / data.clouds.aws.totalSpend) * 100).toFixed(1)}% of your AWS costs ($${ec2Cost.toLocaleString()}). Consider right-sizing, reserved instances, or spot instances.`,
          type: 'rightsize_instance',
          priority: 'high',
          impact: {
            monthlySavings: potentialSavings,
            annualSavings: potentialSavings * 12,
            currency: 'USD'
          },
          effort: 'medium',
          category: 'compute_optimization',
          cloud: 'aws',
          resourceDetails: {
            resourceId: 'ec2-optimization-analysis',
            resourceName: 'EC2 Fleet Optimization',
            resourceType: 'EC2 Analysis',
            region: 'multi-region',
            accountId: 'all-accounts',
            currentConfig: {
              totalInstances: 'multiple',
              totalMonthlyCost: ec2Cost
            },
            recommendedConfig: {
              optimizationStrategy: 'right-sizing + reserved instances',
              estimatedSavings: potentialSavings
            },
            metrics: {
              utilizationRate: 65, // Estimated
              period: '30 days'
            }
          },
          actions: [
            {
              id: 'action-1',
              type: 'api_call',
              description: 'Analyze EC2 instance utilization patterns',
              parameters: { service: 'EC2', analysis: 'utilization' },
              estimatedDuration: '10 minutes',
              riskLevel: 'low',
              rollbackPossible: false
            },
            {
              id: 'action-2',
              type: 'manual_action',
              description: 'Generate right-sizing recommendations',
              parameters: { service: 'EC2', action: 'rightsize' },
              estimatedDuration: '5 minutes',
              riskLevel: 'low',
              rollbackPossible: false
            }
          ],
          status: 'pending'
        };
      }
    },
    {
      id: 'underutilized-rds',
      name: 'Underutilized RDS Instances',
      description: 'Detect RDS instances that may be oversized',
      condition: (data: CostData) => {
        const rdsServices = data.clouds.aws.services.filter(s => s.serviceName === 'RDS');
        return rdsServices.length > 0 && rdsServices[0].totalCost > 20000;
      },
      priority: 'medium',
      generateRecommendation: (data: CostData) => {
        const rdsCost = data.clouds.aws.services.find(s => s.serviceName === 'RDS')?.totalCost || 0;
        const potentialSavings = Math.round(rdsCost * 0.15); // Assume 15% savings
        
        return {
          title: 'Optimize RDS Database Instances',
          description: `Your RDS instances cost $${rdsCost.toLocaleString()}/month. Consider right-sizing databases, implementing read replicas, or using Aurora Serverless for variable workloads.`,
          type: 'rightsize_instance',
          priority: 'medium',
          impact: {
            monthlySavings: potentialSavings,
            annualSavings: potentialSavings * 12,
            currency: 'USD'
          },
          effort: 'medium',
          category: 'storage_optimization',
          cloud: 'aws',
          resourceDetails: {
            resourceId: 'rds-optimization-analysis',
            resourceName: 'RDS Fleet Optimization',
            resourceType: 'RDS Analysis',
            region: 'multi-region',
            accountId: 'all-accounts',
            currentConfig: {
              totalDatabases: 'multiple',
              totalMonthlyCost: rdsCost
            },
            recommendedConfig: {
              optimizationStrategy: 'right-sizing + performance insights',
              estimatedSavings: potentialSavings
            },
            metrics: {
              utilizationRate: 45, // Estimated
              period: '30 days'
            }
          },
          actions: [
            {
              id: 'action-1',
              type: 'api_call',
              description: 'Analyze RDS performance metrics',
              parameters: { service: 'RDS', analysis: 'performance' },
              estimatedDuration: '15 minutes',
              riskLevel: 'low',
              rollbackPossible: false
            }
          ],
          status: 'pending'
        };
      }
    },
    {
      id: 'expensive-bigquery-usage',
      name: 'Expensive BigQuery Usage',
      description: 'Detect high BigQuery costs that could be optimized',
      condition: (data: CostData) => {
        const bigQueryCost = data.clouds.gcp.services.find(s => s.serviceName === 'BigQuery')?.totalCost || 0;
        return bigQueryCost > 8000; // High BigQuery usage
      },
      priority: 'high',
      generateRecommendation: (data: CostData) => {
        const bigQueryCost = data.clouds.gcp.services.find(s => s.serviceName === 'BigQuery')?.totalCost || 0;
        const potentialSavings = Math.round(bigQueryCost * 0.30); // Assume 30% savings
        
        return {
          title: 'Optimize BigQuery Data Processing Costs',
          description: `BigQuery costs are $${bigQueryCost.toLocaleString()}/month. Consider query optimization, partitioning, clustering, and slot reservations for predictable workloads.`,
          type: 'change_storage_tier',
          priority: 'high',
          impact: {
            monthlySavings: potentialSavings,
            annualSavings: potentialSavings * 12,
            currency: 'USD'
          },
          effort: 'medium',
          category: 'storage_optimization',
          cloud: 'gcp',
          resourceDetails: {
            resourceId: 'bigquery-optimization',
            resourceName: 'BigQuery Cost Optimization',
            resourceType: 'BigQuery Analysis',
            region: 'us-central1',
            currentConfig: {
              querySlots: 'on-demand',
              totalMonthlyCost: bigQueryCost
            },
            recommendedConfig: {
              optimizationStrategy: 'slot reservations + query optimization',
              estimatedSavings: potentialSavings
            },
            metrics: {
              utilizationRate: 75,
              period: '30 days'
            }
          },
          actions: [
            {
              id: 'action-1',
              type: 'api_call',
              description: 'Analyze BigQuery query patterns and costs',
              parameters: { service: 'BigQuery', analysis: 'query-optimization' },
              estimatedDuration: '20 minutes',
              riskLevel: 'low',
              rollbackPossible: false
            }
          ],
          status: 'pending'
        };
      }
    },
    {
      id: 'high-github-actions-usage',
      name: 'High GitHub Actions Usage',
      description: 'Detect expensive GitHub Actions usage',
      condition: (data: CostData) => {
        const actionsUsage = data.clouds.github.usage.find(u => u.type === 'actions');
        return actionsUsage ? actionsUsage.cost > 3000 : false;
      },
      priority: 'medium',
      generateRecommendation: (data: CostData) => {
        const actionsUsage = data.clouds.github.usage.find(u => u.type === 'actions');
        const actionsCost = actionsUsage?.cost || 0;
        const potentialSavings = Math.round(actionsCost * 0.20); // Assume 20% savings
        
        return {
          title: 'Optimize GitHub Actions Workflow Efficiency',
          description: `GitHub Actions cost $${actionsCost.toLocaleString()}/month (${actionsUsage?.usage.toLocaleString()} minutes). Consider workflow optimization, caching, and self-hosted runners.`,
          type: 'optimize_workflows',
          priority: 'medium',
          impact: {
            monthlySavings: potentialSavings,
            annualSavings: potentialSavings * 12,
            currency: 'USD'
          },
          effort: 'low',
          category: 'resource_cleanup',
          cloud: 'github',
          resourceDetails: {
            resourceId: 'github-actions-optimization',
            resourceName: 'GitHub Actions Optimization',
            resourceType: 'CI/CD Analysis',
            currentConfig: {
              monthlyMinutes: actionsUsage?.usage || 0,
              monthlyCost: actionsCost
            },
            recommendedConfig: {
              optimizationStrategy: 'workflow caching + self-hosted runners',
              estimatedSavings: potentialSavings
            },
            metrics: {
              utilizationRate: 85,
              period: '30 days'
            }
          },
          actions: [
            {
              id: 'action-1',
              type: 'manual_action',
              description: 'Analyze GitHub Actions workflow efficiency',
              parameters: { service: 'GitHub Actions', analysis: 'workflow-optimization' },
              estimatedDuration: '10 minutes',
              riskLevel: 'low',
              rollbackPossible: false
            }
          ],
          status: 'pending'
        };
      }
    },
    {
      id: 'vertex-ai-ml-costs',
      name: 'High ML Training Costs',
      description: 'Detect expensive ML training workloads',
      condition: (data: CostData) => {
        const vertexAICost = data.clouds.gcp.services.find(s => s.serviceName === 'Vertex AI')?.totalCost || 0;
        return vertexAICost > 20000;
      },
      priority: 'high',
      generateRecommendation: (data: CostData) => {
        const vertexAICost = data.clouds.gcp.services.find(s => s.serviceName === 'Vertex AI')?.totalCost || 0;
        const potentialSavings = Math.round(vertexAICost * 0.35); // Assume 35% savings
        
        return {
          title: 'Optimize ML Training and Inference Costs',
          description: `Vertex AI costs are $${vertexAICost.toLocaleString()}/month. Consider using preemptible instances, custom training jobs, and model optimization techniques.`,
          type: 'optimize_workflows',
          priority: 'high',
          impact: {
            monthlySavings: potentialSavings,
            annualSavings: potentialSavings * 12,
            currency: 'USD'
          },
          effort: 'high',
          category: 'compute_optimization',
          cloud: 'gcp',
          resourceDetails: {
            resourceId: 'vertex-ai-optimization',
            resourceName: 'ML Workload Optimization',
            resourceType: 'Vertex AI Analysis',
            region: 'us-central1',
            currentConfig: {
              trainingJobs: 'multiple',
              monthlyCost: vertexAICost
            },
            recommendedConfig: {
              optimizationStrategy: 'preemptible instances + job scheduling',
              estimatedSavings: potentialSavings
            },
            metrics: {
              utilizationRate: 60,
              period: '30 days'
            }
          },
          actions: [
            {
              id: 'action-1',
              type: 'api_call',
              description: 'Analyze ML training job patterns and resource usage',
              parameters: { service: 'Vertex AI', analysis: 'training-optimization' },
              estimatedDuration: '25 minutes',
              riskLevel: 'low',
              rollbackPossible: false
            }
          ],
          status: 'pending'
        };
      }
    },
    {
      id: 'multi-account-governance',
      name: 'Multi-Account Cost Governance',
      description: 'Detect accounts that need cost governance policies',
      condition: (data: CostData) => {
        // Check if we have multiple accounts with varying cost patterns
        const awsAccounts = data.clouds.aws.accounts || [];
        if (awsAccounts.length < 2) return false;
        
        // Find accounts with high cost growth
        const highGrowthAccounts = awsAccounts.filter(account => 
          account.trend.direction === 'up' && account.trend.percentage > 10
        );
        
        return highGrowthAccounts.length > 0;
      },
      priority: 'medium',
      generateRecommendation: (data: CostData) => {
        const awsAccounts = data.clouds.aws.accounts || [];
        const highGrowthAccounts = awsAccounts.filter(account => 
          account.trend.direction === 'up' && account.trend.percentage > 10
        );
        
        const totalHighGrowthCost = highGrowthAccounts.reduce((sum, acc) => sum + acc.totalCost, 0);
        const potentialSavings = Math.round(totalHighGrowthCost * 0.12); // 12% savings through governance
        
        return {
          title: 'Implement Multi-Account Cost Governance',
          description: `${highGrowthAccounts.length} AWS accounts show high cost growth (>10%). Implement budget alerts, cost allocation tags, and governance policies.`,
          type: 'cleanup_github_resources',
          priority: 'medium',
          impact: {
            monthlySavings: potentialSavings,
            annualSavings: potentialSavings * 12,
            currency: 'USD'
          },
          effort: 'medium',
          category: 'resource_cleanup',
          cloud: 'aws',
          resourceDetails: {
            resourceId: 'multi-account-governance',
            resourceName: 'Cost Governance Implementation',
            resourceType: 'Account Management',
            region: 'global',
            accountId: 'organization',
            currentConfig: {
              totalAccounts: awsAccounts.length,
              highGrowthAccounts: highGrowthAccounts.length,
              combinedMonthlyCost: totalHighGrowthCost
            },
            recommendedConfig: {
              governanceStrategy: 'budget alerts + cost allocation + policies',
              estimatedSavings: potentialSavings
            },
            metrics: {
              utilizationRate: 30, // Current governance maturity score as utilization
              period: '30 days'
            }
          },
          actions: [
            {
              id: 'action-1',
              type: 'api_call',
              description: 'Set up budget alerts for high-growth accounts',
              parameters: { 
                accounts: highGrowthAccounts.map(acc => acc.accountId),
                budgetThreshold: '10%'
              },
              estimatedDuration: '30 minutes',
              riskLevel: 'low',
              rollbackPossible: true
            },
            {
              id: 'action-2',
              type: 'manual_action',
              description: 'Implement cost allocation tagging strategy',
              parameters: { strategy: 'mandatory-tags' },
              estimatedDuration: '60 minutes',
              riskLevel: 'low',
              rollbackPossible: true
            }
          ],
          status: 'pending'
        };
      }
    }
  ];

  generateIntelligentRecommendations(costData: CostData): Recommendation[] {
    const recommendations: Recommendation[] = [];
    const now = new Date().toISOString();

    // Apply each rule to the cost data
    for (const rule of this.rules) {
      try {
        if (rule.condition(costData)) {
          const baseRecommendation = rule.generateRecommendation(costData);
          const recommendation: Recommendation = {
            ...baseRecommendation,
            id: `intelligent-${rule.id}-${Date.now()}`,
            createdAt: now,
            updatedAt: now
          };
          recommendations.push(recommendation);
        }
      } catch (error) {
        console.error(`Error applying rule ${rule.id}:`, error);
      }
    }

    // Sort by priority and potential savings
    return recommendations.sort((a, b) => {
      const priorityWeight = { high: 3, medium: 2, low: 1 };
      const aPriority = priorityWeight[a.priority];
      const bPriority = priorityWeight[b.priority];
      
      if (aPriority !== bPriority) {
        return bPriority - aPriority; // Higher priority first
      }
      
      // If same priority, sort by potential savings
      return b.impact.monthlySavings - a.impact.monthlySavings;
    });
  }

  // Generate summary based on intelligent analysis
  generateIntelligentSummary(recommendations: Recommendation[]) {
    const totalMonthlySavings = recommendations.reduce(
      (sum, rec) => sum + rec.impact.monthlySavings, 0
    );

    return {
      total: recommendations.length,
      byPriority: {
        high: recommendations.filter(r => r.priority === 'high').length,
        medium: recommendations.filter(r => r.priority === 'medium').length,
        low: recommendations.filter(r => r.priority === 'low').length
      },
      byStatus: {
        pending: recommendations.filter(r => r.status === 'pending').length,
        approved: 0,
        in_progress: 0,
        completed: 0,
        rejected: 0,
        failed: 0
      },
      totalPotentialSavings: {
        monthly: totalMonthlySavings,
        annual: totalMonthlySavings * 12,
        currency: 'USD'
      },
      byCloud: {
        aws: recommendations.filter(r => r.cloud === 'aws').length,
        gcp: recommendations.filter(r => r.cloud === 'gcp').length,
        github: recommendations.filter(r => r.cloud === 'github').length
      }
    };
  }
}

export const intelligentRecommendationService = new IntelligentRecommendationService();